import React, { useState, useRef, Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
// Lazy so the recharts subtree (~110 kB gzip) it renders stays out of the
// eager landing-page bundle — it is a below-the-fold marketing preview
// (2026-07 bundle-reduction follow-up).
const DashboardSneakPeek = lazy(() => import('../components/DashboardSneakPeek'));

// ─── Trust badge with hover tooltip ──────────────────────────────────────────
const COMPLIANCE_DATA = {
  act20: {
    icon: '✅', label: 'Supports Wisconsin Act 20 workflows',
    heroBg: 'rgba(59,130,246,0.1)', heroColor: '#2563eb', heroBorder: 'rgba(59,130,246,0.2)',
    description: 'Wisconsin Act 20 early literacy workflows include screening, documentation, and parent communication. Early Literacy App is designed to support those school workflows by helping teachers capture classroom literacy data and prepare print-ready handouts. This tool is not currently on Wisconsin DPI\'s approved universal screener list; districts should verify their compliance approach with their curriculum office.',
    url: 'https://dpi.wi.gov/wi-reads/act-20-4k-and-4kca',
  },
  ferpa: {
    icon: '🔒', label: 'FERPA-supportive data practices',
    heroBg: 'rgba(16,185,129,0.1)', heroColor: '#047857', heroBorder: 'rgba(16,185,129,0.2)',
    description: 'The Family Educational Rights and Privacy Act protects student education records. Early Literacy App is designed to support school-controlled recordkeeping with privacy-minded access controls, Firebase/Google Cloud security controls, and no sale of student data. We are not independently certified for FERPA compliance; schools and districts remain responsible for their own FERPA obligations.',
    url: 'https://studentprivacy.ed.gov/',
  },
  mtss_idea: {
    icon: '📊', label: 'Supports MTSS documentation',
    heroBg: 'rgba(139,92,246,0.1)', heroColor: '#6d28d9', heroBorder: 'rgba(139,92,246,0.2)',
    description: 'Multi-Tiered Systems of Support (MTSS) often relies on timely classroom data. Early Literacy App captures per-letter timing and quarterly growth data that can contribute to district review and support conversations. Final tier, referral, and special education decisions remain the responsibility of the school or district team.',
    url: 'https://sites.ed.gov/idea/',
  },
};

function ComplianceBadge({ dataKey, label, icon, iconColor, bg, color, border, borderRadius = '20px', padding = '6px 16px', fontSize = '0.9rem', fontWeight = 'bold' }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const openTimer = useRef(null);
  const data = COMPLIANCE_DATA[dataKey] || {};
  const displayIcon = icon || data.icon;
  const displayLabel = label || data.label;

  const handleEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    // Small delay before showing so fast mouse passes don't flash the tooltip
    openTimer.current = setTimeout(() => setOpen(true), 1500);
  };
  const handleLeave = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    // Generous delay so the mouse can travel from badge into the tooltip
    closeTimer.current = setTimeout(() => setOpen(false), 200);
  };

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <div
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '8px',
          padding, background: bg, color, borderRadius,
          fontWeight, fontSize, border: `1px solid ${border}`,
          cursor: 'default', userSelect: 'none',
        }}
      >
        {displayIcon && (
          <span style={{ fontSize: '1.1rem', color: iconColor || color }}>{displayIcon}</span>
        )}
        {displayLabel}
      </div>
      {open && data.description && (
        <div
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          style={{
            position: 'absolute', top: 'calc(100% + 8px)', left: '50%',
            transform: 'translateX(-50%)',
            width: '290px', background: 'white', borderRadius: '12px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.14)', border: '1px solid #e2e8f0',
            padding: '14px 16px', zIndex: 300, textAlign: 'left',
            animation: 'tooltipFadeIn 0.2s ease forwards',
          }}
        >
          {/* Arrow */}
          <div style={{
            position: 'absolute', top: '-6px', left: '50%', marginLeft: '-6px',
            width: '12px', height: '12px', background: 'white',
            borderLeft: '1px solid #e2e8f0', borderTop: '1px solid #e2e8f0',
            transform: 'rotate(45deg)',
          }} />
          <p style={{ margin: '0 0 10px 0', fontSize: '0.82em', color: '#334155', lineHeight: '1.6' }}>
            {data.description}
          </p>
          <a href={data.url} target="_blank" rel="noopener noreferrer" style={{
            color: '#3b82f6', fontSize: '0.82em', fontWeight: '700', textDecoration: 'none',
          }}>
            Learn more →
          </a>
        </div>
      )}
    </div>
  );
}

// ─── Static Hero ─────────────────────────────────────────────────────────────
function StaticHero({ scrollToSection }) {
  return (
    <section style={{
      position: 'relative',
      minHeight: 'clamp(380px, 55vw, 580px)',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 60%, #1e3a5f 100%)',
    }}>
      {/* Subtle radial glow */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(ellipse at 30% 60%, rgba(99,102,241,0.18) 0%, transparent 60%)',
        pointerEvents: 'none',
      }} />

      <div style={{
        position: 'relative', zIndex: 2,
        maxWidth: '860px', margin: '0 auto',
        padding: 'clamp(44px, 8vw, 90px) clamp(16px, 5vw, 40px) clamp(52px, 7vw, 80px)',
        textAlign: 'center',
        width: '100%',
        animation: 'heroContentIn 0.7s ease 0.1s forwards',
        opacity: 0,
        boxSizing: 'border-box',
      }}>
        <h1 style={{
          fontSize: 'clamp(2rem, 5.5vw, 4.2rem)',
          fontWeight: '800',
          marginBottom: 'clamp(8px, 1.5vw, 16px)',
          lineHeight: '1.1',
          letterSpacing: 'clamp(-0.3px, -0.12vw, -1.5px)',
          color: 'white',
        }}>
          I built this for my wife.
        </h1>

        <p style={{
          fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)',
          maxWidth: '600px',
          margin: '0 auto clamp(12px, 2vw, 20px)',
          lineHeight: '1.5',
          color: 'rgba(255,255,255,0.75)',
          fontStyle: 'italic',
        }}>
          She's a 4K teacher. You can use it too.
        </p>

        <p style={{
          fontSize: 'clamp(0.9rem, 1.8vw, 1.1rem)',
          maxWidth: '640px',
          margin: '0 auto clamp(28px, 4vw, 44px)',
          lineHeight: '1.7',
          color: 'rgba(255,255,255,0.7)',
        }}>
          Early Literacy App is a tablet-based letter and sound assessment tool for Pre-K and Kindergarten teachers. One tap per letter. Automatic quarterly growth charts. Print-ready parent conference reports. No clipboards.
        </p>

        <div className="hero-cta-strip" style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => scrollToSection('pricing-section')}
            className="cta-primary"
            style={{ padding: 'clamp(12px, 1.8vw, 16px) clamp(28px, 4vw, 44px)', borderRadius: '10px', fontSize: 'clamp(0.95rem, 1.8vw, 1.05rem)', fontWeight: 'bold', cursor: 'pointer' }}
          >
            Try it free
          </button>
          <button
            onClick={() => scrollToSection('story-section')}
            style={{ padding: 'clamp(10px, 1.8vw, 14px) clamp(28px, 4vw, 44px)', borderRadius: '10px', fontSize: 'clamp(0.95rem, 1.8vw, 1.05rem)', fontWeight: 'bold', cursor: 'pointer', background: 'transparent', border: '2px solid rgba(255,255,255,0.5)', color: 'white', transition: 'border-color 0.2s' }}
          >
            Read the story
          </button>
        </div>
      </div>
    </section>
  );
}

const SHOW_TESTIMONIALS = false;

function LandingPage() {
    const [demoForm, setDemoForm] = useState({ name: '', email: '', district: '', message: '', role: '' });
    const [demoStatus, setDemoStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'

    const handleDemoSubmit = async (e) => {
        e.preventDefault();
        setDemoStatus('submitting');
        try {
            await addDoc(collection(db, 'demoRequests'), {
                ...demoForm,
                timestamp: serverTimestamp(),
            });
            setDemoStatus('success');
            setDemoForm({ name: '', email: '', district: '', message: '', role: '' });
        } catch (error) {
            console.error("Error submitting demo request:", error);
            setDemoStatus('error');
        }
    };

    const scrollToSection = (id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    };
    return (
        <div className="landing-page" style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#fafbfc',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
        }}>
            
            {/* Embedded styles for keyframes & premium effects */}
            <style>
                {`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;800&display=swap');
                
                .glass-header {
                    background: rgba(255, 255, 255, 0.85);
                    backdrop-filter: blur(12px);
                    border-bottom: 1px solid rgba(0,0,0,0.08);
                    position: sticky;
                    top: 0;
                    z-index: 1000;
                }
                .hero-gradient {
                    background: linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%);
                    position: relative;
                    overflow: hidden;
                }
                .hero-gradient::before {
                    content: '';
                    position: absolute;
                    top: -50%; right: -20%;
                    width: 800px; height: 800px;
                    background: radial-gradient(circle, rgba(99,102,241,0.1) 0%, rgba(255,255,255,0) 70%);
                    border-radius: 50%;
                }
                .cta-primary {
                    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
                    color: white;
                    border: none;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    text-decoration: none;
                }
                .btn-secondary {
                    background: transparent;
                    color: #3b82f6;
                    border: 2px solid #3b82f6;
                    transition: all 0.3s ease;
                    text-decoration: none;
                }
                .btn-secondary:hover {
                    background: rgba(59, 130, 246, 0.05);
                    transform: translateY(-2px);
                }
                .feature-card {
                    background: white;
                    border: 1px solid rgba(0,0,0,0.05);
                    transition: all 0.3s ease;
                }
                .feature-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 20px 40px -10px rgba(0,0,0,0.08);
                    border-color: rgba(59, 130, 246, 0.3);
                }
                .animate-fade-in-up {
                    animation: fadeInUp 0.8s ease forwards;
                    opacity: 0;
                    transform: translateY(20px);
                }
                @keyframes fadeInUp {
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes tooltipFadeIn {
                    from { opacity: 0; transform: translateX(-50%) translateY(-6px); }
                    to   { opacity: 1; transform: translateX(-50%) translateY(0); }
                }
                @keyframes heroBgIn {
                    from { opacity: 0.4; transform: scale(1.06) translateX(40px); }
                    to   { opacity: 1;   transform: scale(1)    translateX(0); }
                }
                @keyframes heroContentIn {
                    from { opacity: 0; transform: translateY(18px); }
                    to   { opacity: 1; transform: translateY(0); }
                }

                /* ── Responsive layout ─────────────────────────────── */
                * { box-sizing: border-box; }

                @media (max-width: 768px) {
                    .glass-header { padding: 12px 16px !important; }
                    .feature-card { padding: 24px !important; }
                    .product-tour-grid { grid-template-columns: 1fr !important; }
                    .compliance-card  { padding: 20px !important; }
                    .pricing-card     { padding: 28px 20px !important; }
                    .demo-form-card   { padding: 28px 20px !important; }
                }
                @media (max-width: 480px) {
                    .glass-header { padding: 10px 12px !important; }
                    .hero-cta-strip { flex-direction: column !important; align-items: stretch !important; }
                    .hero-cta-strip button { width: 100% !important; }
                }
                `}
            </style>

            {/* Header */}
            <header className="glass-header" style={{
                padding: '15px 40px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px', letterSpacing: '-0.5px' }}>
                    <span style={{ 
                        background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)', 
                        color: 'white', 
                        padding: '6px 12px', 
                        borderRadius: '8px',
                        boxShadow: '0 4px 10px rgba(59,130,246,0.3)'
                    }}>EL</span>
                    Early Literacy App
                </div>
                
                <nav style={{ gap: '30px', alignItems: 'center', display: window.innerWidth < 768 ? 'none' : 'flex' }}>
                    <button onClick={() => scrollToSection('story-section')} style={{ background: 'none', border: 'none', color: '#475569', fontWeight: '600', fontSize: '1rem', cursor: 'pointer', transition: 'color 0.2s' }}>Our Story</button>
                    <button onClick={() => scrollToSection('features-section')} style={{ background: 'none', border: 'none', color: '#475569', fontWeight: '600', fontSize: '1rem', cursor: 'pointer', transition: 'color 0.2s' }}>Features</button>
                    <button onClick={() => scrollToSection('pricing-section')} style={{ background: 'none', border: 'none', color: '#475569', fontWeight: '600', fontSize: '1rem', cursor: 'pointer', transition: 'color 0.2s' }}>Pricing</button>
                </nav>

                <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                    <Link to="/login" style={{
                        color: '#3b82f6',
                        fontWeight: '600',
                        textDecoration: 'none',
                    }}>
                        Teacher Login
                    </Link>
                    <button onClick={() => scrollToSection('pricing-section')} className="cta-primary" style={{
                        padding: '10px 24px',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        fontWeight: '600',
                        cursor: 'pointer'
                    }}>
                        Try it free
                    </button>
                </div>
            </header>

            <main style={{ flex: 1 }}>
                
                {/* Hero Section */}
                <StaticHero scrollToSection={scrollToSection} />

                {/* Static strip: compliance badges + CTA buttons — always visible below banner */}
                <div style={{
                    background: 'white',
                    padding: 'clamp(18px, 3.5vw, 28px) clamp(16px, 4vw, 24px)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '16px',
                    borderBottom: '1px solid #e2e8f0',
                }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px' }}>
                        <ComplianceBadge dataKey="act20"
                            bg="rgba(59,130,246,0.08)" color="#2563eb" border="rgba(59,130,246,0.2)" borderRadius="20px" />
                        <ComplianceBadge dataKey="ferpa"
                            bg="rgba(16,185,129,0.08)" color="#047857" border="rgba(16,185,129,0.2)" borderRadius="20px" />
                        <ComplianceBadge dataKey="mtss_idea"
                            bg="rgba(139,92,246,0.08)" color="#6d28d9" border="rgba(139,92,246,0.2)" borderRadius="20px" />
                    </div>
                    <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', justifyContent: 'center' }}>
                        <button onClick={() => scrollToSection('pricing-section')} className="cta-primary" style={{
                            padding: 'clamp(10px, 1.5vw, 14px) clamp(24px, 4vw, 38px)',
                            borderRadius: '10px', fontSize: 'clamp(0.95rem, 1.8vw, 1.05rem)',
                            fontWeight: 'bold', cursor: 'pointer',
                        }}>
                            Try It Free
                        </button>
                        <button onClick={() => scrollToSection('demo-section')} className="btn-secondary" style={{
                            padding: 'clamp(8px, 1.5vw, 12px) clamp(24px, 4vw, 38px)',
                            borderRadius: '10px', fontSize: 'clamp(0.95rem, 1.8vw, 1.05rem)',
                            fontWeight: 'bold', cursor: 'pointer',
                        }}>
                            Contact us
                        </button>
                    </div>
                </div>

                {/* Story Section */}
                <section id="story-section" style={{ padding: 'clamp(48px, 8vw, 80px) clamp(16px, 4vw, 20px)', background: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
                    <div style={{ maxWidth: '720px', margin: '0 auto' }}>
                        <h2 style={{ fontSize: '2.2rem', fontWeight: '800', color: '#0f172a', marginBottom: '32px', letterSpacing: '-0.5px' }}>
                            Why I built this
                        </h2>
                        <p style={{ fontSize: '1.15rem', color: '#475569', lineHeight: '1.85', marginBottom: '20px' }}>
                            My wife teaches 4K in Wisconsin. Every fall, winter, and spring she sits down with each student and goes through 26 uppercase letters, 26 lowercase letters, and 26 letter sounds — one at a time, marking Correct or Incorrect on a clipboard. Then she transcribes those marks into a spreadsheet. Then she prints reports for parent conferences.
                        </p>
                        <p style={{ fontSize: '1.15rem', color: '#475569', lineHeight: '1.85', marginBottom: '20px' }}>
                            The whole process takes hours she doesn't have. And most assessment tools just weren't designed with her in mind — they were built for dashboards and district reports, not for a teacher sitting cross-legged on a classroom rug with a five-year-old.
                        </p>
                        <p style={{ fontSize: '1.15rem', color: '#475569', lineHeight: '1.85', marginBottom: '20px' }}>
                            So I built something for her. A big letter appears on the screen. She taps I (Incorrect) or C (Correct). The app records everything — including how long the student hesitated. At the end, she has a growth chart and a print-ready report for every student. No clipboards. No spreadsheets. No transcription.
                        </p>
                        <p style={{ fontSize: '1.15rem', color: '#475569', lineHeight: '1.85', marginBottom: '20px' }}>
                            It started as a weekend project. Other teachers in her building started asking about it. Then teachers at other schools. This is still a small, personal tool — I'm one person building it with direct feedback from real classrooms. It isn't trying to be everything. It's trying to do one thing well.
                        </p>
                        <p style={{ fontSize: '1.1rem', color: '#64748b', lineHeight: '1.85', fontStyle: 'italic', marginBottom: '32px' }}>
                            If you're a Pre-K or Kindergarten teacher and this sounds useful, I'd love for you to try it. And if something doesn't work the way you'd expect, email me directly — I read every message.
                        </p>
                        <p style={{ fontSize: '1rem', color: '#334155', fontWeight: '700' }}>
                            — Kemal
                        </p>
                    </div>
                </section>

                <div style={{ maxWidth: '1200px', margin: '0 auto', padding: 'clamp(56px, 9vw, 100px) clamp(16px, 4vw, 20px)' }}>

                    {/* Premium Feature Grid */}
                    <section id="features-section" style={{ marginBottom: '100px' }}>
                        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                            <h2 style={{ fontSize: '2.5rem', fontWeight: '800', color: '#0f172a', marginBottom: '16px', letterSpacing: '-1px' }}>
                                What it does
                            </h2>
                            <p style={{ fontSize: '1.25rem', color: '#64748b', maxWidth: '600px', margin: '0 auto' }}>
                                Six things. Each one is something a teacher asked for.
                            </p>
                        </div>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))',
                            gap: 'clamp(16px, 3vw, 30px)'
                        }}>
                            {/* Feature 1: Eliminate Paperwork */}
                            <div className="feature-card" style={{ padding: 'clamp(22px, 4vw, 40px)', borderRadius: '20px' }}>
                                <div style={{ fontSize: '3rem', marginBottom: '20px' }}>✂️</div>
                                <h3 style={{ fontSize: '1.5rem', fontWeight: '800', marginBottom: '12px', color: '#0f172a' }}>Eliminate Paperwork</h3>
                                <p style={{ color: '#64748b', lineHeight: '1.7', fontSize: '1.1rem' }}>
                                    Throw away the clipboards and manila folders. Record student responses directly on your tablet with an interface so simple, it feels like a game.
                                </p>
                            </div>
                            
                            {/* Feature 2: Fluency & Intervention */}
                            <div className="feature-card" style={{ padding: 'clamp(22px, 4vw, 40px)', borderRadius: '20px' }}>
                                <div style={{ fontSize: '3rem', marginBottom: '20px' }}>🎯</div>
                                <h3 style={{ fontSize: '1.5rem', fontWeight: '800', marginBottom: '12px', color: '#0f172a' }}>Fluency-Aware Interventions</h3>
                                <p style={{ color: '#64748b', lineHeight: '1.7', fontSize: '1.1rem' }}>
                                    Per-letter response timing. A hesitation longer than 3 seconds is flagged visually, so you can see not just what a student knows but how fluent they are. Teachers use this to inform small-group instruction.
                                </p>
                            </div>

                            {/* Feature 3: Growth Reports */}
                            <div className="feature-card" style={{ padding: 'clamp(22px, 4vw, 40px)', borderRadius: '20px' }}>
                                <div style={{ fontSize: '3rem', marginBottom: '20px' }}>📈</div>
                                <h3 style={{ fontSize: '1.5rem', fontWeight: '800', marginBottom: '12px', color: '#0f172a' }}>Quarterly Growth Tracking</h3>
                                <p style={{ color: '#64748b', lineHeight: '1.7', fontSize: '1.1rem' }}>
                                    Charts automatically compare Q1–Q4 results side-by-side. Add teacher narrative comments to any report. One click generates a print-ready PDF handout that can support parent-teacher conferences and school notification workflows.
                                </p>
                            </div>

                            {/* Feature 4: Practice Mode */}
                            <div className="feature-card" style={{ padding: 'clamp(22px, 4vw, 40px)', borderRadius: '20px' }}>
                                <div style={{ fontSize: '3rem', marginBottom: '20px' }}>🎮</div>
                                <h3 style={{ fontSize: '1.5rem', fontWeight: '800', marginBottom: '12px', color: '#0f172a' }}>Dedicated Practice Mode</h3>
                                <p style={{ color: '#64748b', lineHeight: '1.7', fontSize: '1.1rem' }}>
                                    A full-screen flashcard mode separate from assessments. Display uppercase, lowercase, or both letters simultaneously. Keyboard-shortcut driven for fast classroom pacing—works on tablets, smartboards, or laptops.
                                </p>
                            </div>

                            {/* Feature 5: Security */}
                            <div className="feature-card" style={{ padding: 'clamp(22px, 4vw, 40px)', borderRadius: '20px' }}>
                                <div style={{ fontSize: '3rem', marginBottom: '20px' }}>🛡️</div>
                                <h3 style={{ fontSize: '1.5rem', fontWeight: '800', marginBottom: '12px', color: '#0f172a' }}>Security You Can Explain</h3>
                                <p style={{ color: '#64748b', lineHeight: '1.7', fontSize: '1.1rem' }}>
                                    Built with privacy-minded access controls for school data. Access controls are designed so authorized users can access only student, class, school, or district data assigned to their role. Admin views include activity context for review, and multi-factor authentication is available.
                                </p>
                            </div>

                            {/* Feature 6: Real Classrooms */}
                            <div className="feature-card" style={{ padding: 'clamp(22px, 4vw, 40px)', borderRadius: '20px' }}>
                                <div style={{ fontSize: '3rem', marginBottom: '20px' }}>🏫</div>
                                <h3 style={{ fontSize: '1.5rem', fontWeight: '800', marginBottom: '12px', color: '#0f172a' }}>Built for Real Classrooms</h3>
                                <p style={{ color: '#64748b', lineHeight: '1.7', fontSize: '1.1rem' }}>
                                    Configure your own school's Q1–Q4 academic calendar. Bulk-upload student rosters from a CSV. Add co-teachers or educational assistants. Built so one classroom can use it — or a whole grade level.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Embedded Mock App Screens (Visual Proof / 3-Step Tour) */}
                    <section style={{ marginBottom: 'clamp(60px, 9vw, 100px)', padding: 'clamp(32px, 5vw, 60px) clamp(16px, 4vw, 40px)', background: '#f8fafc', borderRadius: '24px', border: '1px solid #e2e8f0' }}>
                        <div style={{ textAlign: 'center', marginBottom: '50px' }}>
                            <h2 style={{ fontSize: '2.5rem', fontWeight: '800', color: '#0f172a', marginBottom: '16px', letterSpacing: '-1px' }}>
                                Product Tour
                            </h2>
                            <p style={{ fontSize: '1.2rem', color: '#64748b', maxWidth: '700px', margin: '0 auto' }}>
                                A complete workflow from student assessment to family connection.
                            </p>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))', gap: 'clamp(16px, 3vw, 30px)' }}>
                            
                            {/* Step 1: Rapid Screening */}
                            <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.06)', overflow: 'hidden', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                <div style={{ background: '#3b82f6', color: 'white', padding: '12px 20px', fontWeight: 'bold', fontSize: '1.1rem' }}>
                                    Step 1: Rapid Screening
                                </div>
                                <div style={{ padding: '40px 24px', textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative' }}>
                                    <div style={{ position: 'absolute', top: '15px', right: '15px', background: '#e2e8f0', color: '#475569', padding: '4px 8px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 'bold' }}>Offline Ready</div>
                                    <div style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>Assessing: Sophie L.</div>
                                    <div style={{ fontSize: '6rem', fontWeight: '800', color: '#0f172a', marginBottom: '30px', lineHeight: '1' }}>M</div>
                                    <div style={{ display: 'flex', gap: '24px', justifyContent: 'center' }}>
                                        <div style={{ padding: '20px 40px', borderRadius: '8px', background: '#e0e0e0', border: '1px solid #ccc', fontSize: '2rem', fontWeight: 'bold', color: '#000', fontFamily: 'sans-serif' }}>I</div>
                                        <div style={{ padding: '20px 40px', borderRadius: '8px', background: '#e0e0e0', border: '1px solid #ccc', fontSize: '2rem', fontWeight: 'bold', color: '#000', fontFamily: 'sans-serif' }}>C</div>
                                    </div>
                                </div>
                                <div style={{ padding: '20px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', fontSize: '0.95rem', color: '#475569', lineHeight: '1.5' }}>
                                    Tap through letters on a tablet or laptop. Mark correct or incorrect. Auto-saves as you go.
                                </div>
                            </div>

                            {/* Step 2: Instructional Insight */}
                            <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.06)', overflow: 'hidden', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                <div style={{ background: '#8b5cf6', color: 'white', padding: '12px 20px', fontWeight: 'bold', fontSize: '1.1rem' }}>
                                    Step 2: Instructional Insight
                                </div>
                                <div style={{ padding: '24px', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Suspense fallback={<div style={{ minHeight: 200 }} />}>
                                        <DashboardSneakPeek />
                                    </Suspense>
                                </div>
                                <div style={{ padding: '20px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', fontSize: '0.95rem', color: '#475569', lineHeight: '1.5' }}>
                                    See your class at a glance — who's at mastery, who's emerging, who needs support. Click any student for detail.
                                </div>
                            </div>

                            {/* Step 3: Family Connection */}
                            <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.06)', overflow: 'hidden', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                <div style={{ background: '#10b981', color: 'white', padding: '12px 20px', fontWeight: 'bold', fontSize: '1.1rem' }}>
                                    Step 3: Family Connection
                                </div>
                                <div style={{ padding: '30px', flex: 1, background: '#f1f5f9', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    {/* Printable PDF Mockup */}
                                    <div style={{ width: '100%', maxWidth: '250px', background: 'white', padding: '20px', borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: '1px solid #cbd5e1' }}>
                                        <div style={{ height: '8px', background: '#e2e8f0', width: '60%', marginBottom: '16px', borderRadius: '4px' }}></div>
                                        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                                            <div style={{ flex: 1, height: '60px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '4px' }}></div>
                                            <div style={{ flex: 1, height: '60px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '4px' }}></div>
                                        </div>
                                        <div style={{ height: '6px', background: '#f1f5f9', width: '100%', marginBottom: '8px', borderRadius: '3px' }}></div>
                                        <div style={{ height: '6px', background: '#f1f5f9', width: '80%', marginBottom: '8px', borderRadius: '3px' }}></div>
                                        <div style={{ height: '6px', background: '#f1f5f9', width: '90%', marginBottom: '24px', borderRadius: '3px' }}></div>
                                        
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{ display: 'inline-block', padding: '6px 12px', background: '#fdf2f8', color: '#be185d', fontSize: '0.75rem', fontWeight: 'bold', borderRadius: '12px', border: '1px solid #fbcfe8' }}>
                                                🖨️ Meeting-Ready PDF
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ padding: '20px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', fontSize: '0.95rem', color: '#475569', lineHeight: '1.5' }}>
                                    Generate a one-page PDF for parent conferences. Add your own comments before printing.
                                </div>
                            </div>

                        </div>
                    </section>
                </div>
                
                {/* Social Proof / Early Voices */}
                {SHOW_TESTIMONIALS ? (
                    // TODO: When real testimonials are collected from beta teachers,
                    // replace this placeholder with actual teacher quotes.
                    // Each testimonial should include: full name (with permission), school, state,
                    // and a direct quote about a specific feature or outcome.
                    <section style={{ background: '#0f172a', padding: 'clamp(60px, 9vw, 100px) clamp(16px, 4vw, 20px)', textAlign: 'center', borderTop: '1px solid #1e293b' }}>
                        <p style={{ color: '#64748b' }}>Testimonials coming soon.</p>
                    </section>
                ) : (
                    <section style={{
                        background: '#0f172a',
                        padding: 'clamp(60px, 9vw, 100px) clamp(16px, 4vw, 20px)',
                        textAlign: 'center',
                        borderTop: '1px solid #1e293b'
                    }}>
                        <div style={{ maxWidth: '680px', margin: '0 auto' }}>
                            <div style={{ fontSize: '2.5rem', marginBottom: '24px' }}>🌱</div>
                            <h2 style={{ fontSize: '2rem', fontWeight: '800', color: 'white', marginBottom: '20px', letterSpacing: '-0.5px' }}>
                                Early voices
                            </h2>
                            <p style={{ fontSize: '1.1rem', color: '#94a3b8', lineHeight: '1.8', marginBottom: '24px' }}>
                                Early Literacy App is currently in beta with one Wisconsin elementary school. We're gathering feedback from teachers and will share real testimonials here as they come in.
                            </p>
                            <p style={{ fontSize: '1rem', color: '#64748b', lineHeight: '1.75' }}>
                                If you're a 4K or Kindergarten teacher who'd like to try it and share your honest experience, email{' '}
                                <a href="mailto:support@literacyassessments.com" style={{ color: '#38bdf8', textDecoration: 'none', fontWeight: '600' }}>
                                    support@literacyassessments.com
                                </a>
                                {' '}— we're looking for 5 more teachers to join the beta this spring.
                            </p>
                        </div>
                    </section>
                )}

                {/* Compliance & Legislation Section */}
                <section style={{ padding: 'clamp(56px, 9vw, 100px) clamp(16px, 4vw, 20px)', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                    <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
                        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                            <h2 style={{ fontSize: '2.5rem', fontWeight: '800', color: '#0f172a', marginBottom: '16px', letterSpacing: '-1px' }}>
                                Designed for School Workflows. Built for Teachers.
                            </h2>
                            <p style={{ fontSize: '1.2rem', color: '#64748b', maxWidth: '700px', margin: '0 auto' }}>
                                Every feature is designed to help early childhood educators document literacy progress, prepare family-facing reports, and support school review workflows without adding extra paperwork.
                            </p>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: 'clamp(14px, 2.5vw, 24px)' }}>

                            {/* WI Act 20 */}
                            <div style={{ background: 'white', borderRadius: '16px', padding: 'clamp(18px, 3.5vw, 32px)', border: '2px solid #bfdbfe', boxShadow: '0 4px 12px rgba(59,130,246,0.06)' }}>
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '4px 12px', background: '#eff6ff', color: '#1d4ed8', borderRadius: '12px', fontWeight: '700', fontSize: '0.8rem', marginBottom: '16px' }}>
                                    ✅ WI Act 20 Support
                                </div>
                                <h3 style={{ fontSize: '1.15rem', fontWeight: '800', color: '#0f172a', marginBottom: '10px' }}>Early Literacy Screening Support</h3>
                                <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: '1.7', margin: 0 }}>
                                    Helps schools prepare print-ready early literacy summaries that can support Act 20 parent notification workflows. Districts remain responsible for required notices and for verifying their choice of tool against DPI's approved screener list.
                                </p>
                            </div>

                            {/* FERPA */}
                            <div style={{ background: 'white', borderRadius: '16px', padding: 'clamp(18px, 3.5vw, 32px)', border: '2px solid #bbf7d0', boxShadow: '0 4px 12px rgba(16,185,129,0.06)' }}>
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '4px 12px', background: '#f0fdf4', color: '#15803d', borderRadius: '12px', fontWeight: '700', fontSize: '0.8rem', marginBottom: '16px' }}>
                                    🔒 FERPA Support
                                </div>
                                <h3 style={{ fontSize: '1.15rem', fontWeight: '800', color: '#0f172a', marginBottom: '10px' }}>Student Records Protection</h3>
                                <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: '1.7', margin: 0 }}>
                                    Student data remains school-controlled. Access controls are designed so authorized users can access only student, class, school, or district data assigned to their role. The service uses Firebase/Google Cloud security controls and keeps administrative activity context for review. School districts remain responsible for their overall FERPA posture.
                                </p>
                            </div>

                            {/* IDEA / Child Find */}
                            <div style={{ background: 'white', borderRadius: '16px', padding: 'clamp(18px, 3.5vw, 32px)', border: '2px solid #e9d5ff', boxShadow: '0 4px 12px rgba(139,92,246,0.06)' }}>
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '4px 12px', background: '#faf5ff', color: '#6d28d9', borderRadius: '12px', fontWeight: '700', fontSize: '0.8rem', marginBottom: '16px' }}>
                                    📋 IDEA / Child Find Support
                                </div>
                                <h3 style={{ fontSize: '1.15rem', fontWeight: '800', color: '#0f172a', marginBottom: '10px' }}>Early Identification Support</h3>
                                <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: '1.7', margin: 0 }}>
                                    Per-letter timing and hesitation flags can provide classroom evidence that teachers may contribute to Child Find discussions or IEP team review. The tool does not replace formal special education evaluation processes.
                                </p>
                            </div>

                            {/* MTSS */}
                            <div style={{ background: 'white', borderRadius: '16px', padding: 'clamp(18px, 3.5vw, 32px)', border: '2px solid #fed7aa', boxShadow: '0 4px 12px rgba(249,115,22,0.06)' }}>
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '4px 12px', background: '#fff7ed', color: '#c2410c', borderRadius: '12px', fontWeight: '700', fontSize: '0.8rem', marginBottom: '16px' }}>
                                    📊 MTSS Tier 1
                                </div>
                                <h3 style={{ fontSize: '1.15rem', fontWeight: '800', color: '#0f172a', marginBottom: '10px' }}>Universal Screening Documentation</h3>
                                <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: '1.7', margin: 0 }}>
                                    Dashboard groups students into Mastery, Emerging, and Needs Support categories after each assessment. Quarterly longitudinal charts can contribute to Tier 1 review and instructional planning. Final tier assignments remain a district decision.
                                </p>
                            </div>

                        </div>
                    </div>
                </section>

                {/* Transparent Pricing Section */}
                <section id="pricing-section" style={{ padding: 'clamp(56px, 9vw, 100px) clamp(16px, 4vw, 20px)', background: 'white', borderTop: '1px solid #e2e8f0' }}>
                    <div style={{ maxWidth: '1140px', margin: '0 auto', textAlign: 'center' }}>
                        <h2 style={{ fontSize: '2.5rem', fontWeight: '800', color: '#0f172a', marginBottom: '20px', letterSpacing: '-1px' }}>
                            Simple, Transparent Pricing.
                        </h2>
                        <p style={{ fontSize: '1.25rem', color: '#475569', marginBottom: '8px', maxWidth: '680px', marginLeft: 'auto', marginRight: 'auto', lineHeight: '1.6' }}>
                            One teacher license covers all of that teacher's classes and students — unlimited.
                        </p>
                        <p style={{ fontSize: '1.05rem', color: '#64748b', marginBottom: 'clamp(36px, 5vw, 52px)' }}>
                            14-day free trial, no credit card required.
                        </p>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))', gap: 'clamp(16px, 2.5vw, 28px)', textAlign: 'left', alignItems: 'stretch' }}>

                            {/* ── Teacher (featured) ── */}
                            <div className="pricing-card" style={{ background: 'white', padding: 'clamp(24px, 4vw, 40px)', borderRadius: '24px', boxShadow: '0 20px 40px rgba(59,130,246,0.10)', border: '2px solid #3b82f6', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                                <div style={{ position: 'absolute', top: '-13px', left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: 'white', fontSize: '0.72rem', fontWeight: '800', letterSpacing: '0.5px', textTransform: 'uppercase', padding: '5px 14px', borderRadius: '999px', whiteSpace: 'nowrap' }}>For Individual Teachers</div>
                                <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>Teacher</div>
                                <div style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '18px' }}>Individual Teacher License — one educator seat</div>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '6px' }}>
                                    <span style={{ fontSize: '2.75rem', fontWeight: '800', color: '#0f172a' }}>$69</span>
                                    <span style={{ fontSize: '1.1rem', color: '#64748b', fontWeight: '600' }}>/ teacher / year</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                                    <span style={{ display: 'inline-block', background: '#eff6ff', color: '#1d4ed8', fontWeight: '700', fontSize: '0.78rem', padding: '3px 10px', borderRadius: '999px' }}>Founding rate</span>
                                    <span style={{ fontSize: '0.95rem', color: '#94a3b8', textDecoration: 'line-through' }}>reg. $89/yr</span>
                                </div>
                                <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '22px' }}>Annual billing. Unlimited classes and students.</div>
                                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px 0', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '13px' }}>
                                    <li style={{ display: 'flex', gap: '12px', color: '#334155', fontSize: '1.02rem' }}>✅ <span>Unlimited student rosters & classes</span></li>
                                    <li style={{ display: 'flex', gap: '12px', color: '#334155', fontSize: '1.02rem' }}>✅ <span>Full suite of Alphabet & Sound assessments</span></li>
                                    <li style={{ display: 'flex', gap: '12px', color: '#334155', fontSize: '1.02rem' }}>✅ <span>Per-letter fluency timing with hesitation flags</span></li>
                                    <li style={{ display: 'flex', gap: '12px', color: '#334155', fontSize: '1.02rem' }}>✅ <span>Quarterly growth tracking with longitudinal charts</span></li>
                                    <li style={{ display: 'flex', gap: '12px', color: '#334155', fontSize: '1.02rem' }}>✅ <span>Teacher comments on every report</span></li>
                                    <li style={{ display: 'flex', gap: '12px', color: '#334155', fontSize: '1.02rem' }}>✅ <span>Dedicated Practice Mode for classroom use</span></li>
                                    <li style={{ display: 'flex', gap: '12px', color: '#334155', fontSize: '1.02rem' }}>✅ <span>Automated PDF parent-conference handouts</span></li>
                                    <li style={{ display: 'flex', gap: '12px', color: '#334155', fontSize: '1.02rem' }}>✅ <span>Co-teacher & Educational Assistant management</span></li>
                                </ul>
                                <button onClick={() => scrollToSection('demo-section')} className="cta-primary" style={{ width: '100%', padding: '16px', borderRadius: '12px', fontSize: '1.05rem', fontWeight: 'bold', cursor: 'pointer', marginTop: 'auto' }}>
                                    Start 14-Day Free Trial
                                </button>
                                <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '12px', textAlign: 'center' }}>14-day free trial — no credit card required.</div>
                            </div>

                            {/* ── School ── */}
                            <div className="pricing-card" style={{ background: 'white', padding: 'clamp(24px, 4vw, 40px)', borderRadius: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#6d28d9', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>School</div>
                                <div style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '18px' }}>Whole-building license</div>
                                <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#0f172a', marginBottom: '22px', lineHeight: '1.3' }}>Contact us for school pricing.</div>
                                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px 0', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '13px' }}>
                                    <li style={{ display: 'flex', gap: '12px', color: '#334155', fontSize: '1.02rem' }}>🏫 <span>Whole-building license — every teacher included</span></li>
                                    <li style={{ display: 'flex', gap: '12px', color: '#334155', fontSize: '1.02rem' }}>💸 <span>Volume pricing</span></li>
                                    <li style={{ display: 'flex', gap: '12px', color: '#334155', fontSize: '1.02rem' }}>♾️ <span>Every teacher gets unlimited classes &amp; students</span></li>
                                </ul>
                                <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <a href="tel:+12625267582" className="cta-primary" style={{ display: 'block', padding: '14px', borderRadius: '12px', fontSize: '1.05rem', fontWeight: 'bold', cursor: 'pointer', textAlign: 'center', textDecoration: 'none' }}>📞 Call us</a>
                                    <a href="mailto:support@literacyassessments.com" className="btn-secondary" style={{ display: 'block', padding: '13px', borderRadius: '12px', fontSize: '1.05rem', fontWeight: 'bold', cursor: 'pointer', textAlign: 'center', textDecoration: 'none' }}>✉️ Email us</a>
                                    <div style={{ fontSize: '0.85rem', color: '#64748b', textAlign: 'center', marginTop: '2px' }}>+1 (262) 526-7582</div>
                                </div>
                            </div>

                            {/* ── District ── */}
                            <div className="pricing-card" style={{ background: 'white', padding: 'clamp(24px, 4vw, 40px)', borderRadius: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#0f766e', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>District</div>
                                <div style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '18px' }}>District-wide licensing</div>
                                <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#0f172a', marginBottom: '22px', lineHeight: '1.3' }}>Contact us for district pricing.</div>
                                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px 0', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '13px' }}>
                                    <li style={{ display: 'flex', gap: '12px', color: '#334155', fontSize: '1.02rem' }}>🗂️ <span>District-wide licensing</span></li>
                                    <li style={{ display: 'flex', gap: '12px', color: '#334155', fontSize: '1.02rem' }}>🤝 <span>Onboarding support</span></li>
                                    <li style={{ display: 'flex', gap: '12px', color: '#334155', fontSize: '1.02rem' }}>📋 <span>DPA / Wisconsin Act 20 compliance</span></li>
                                </ul>
                                <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <a href="tel:+12625267582" className="cta-primary" style={{ display: 'block', padding: '14px', borderRadius: '12px', fontSize: '1.05rem', fontWeight: 'bold', cursor: 'pointer', textAlign: 'center', textDecoration: 'none' }}>📞 Call us</a>
                                    <a href="mailto:support@literacyassessments.com" className="btn-secondary" style={{ display: 'block', padding: '13px', borderRadius: '12px', fontSize: '1.05rem', fontWeight: 'bold', cursor: 'pointer', textAlign: 'center', textDecoration: 'none' }}>✉️ Email us</a>
                                    <div style={{ fontSize: '0.85rem', color: '#64748b', textAlign: 'center', marginTop: '2px' }}>+1 (262) 526-7582</div>
                                </div>
                            </div>

                        </div>
                    </div>
                </section>

                {/* Lead Generation Form */}
                <section id="demo-section" style={{ padding: 'clamp(56px, 9vw, 100px) clamp(16px, 4vw, 20px)', background: 'white' }}>
                    <div style={{ maxWidth: '600px', margin: '0 auto', background: '#ffffff', borderRadius: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.08)', padding: 'clamp(24px, 5vw, 50px)', border: '1px solid #e2e8f0' }}>
                        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                            <h2 style={{ fontSize: '2rem', fontWeight: '800', color: '#0f172a', marginBottom: '12px', letterSpacing: '-0.5px' }}>Questions? Beta access? Drop a note.</h2>
                            <p style={{ color: '#64748b', fontSize: '1.1rem' }}>I read every email personally. This goes straight to me — Kemal, the founder. Expect a reply within 2 business days.</p>
                        </div>
                        
                        {demoStatus === 'success' ? (
                            <div style={{ padding: '30px', background: '#f0fdf4', borderRadius: '12px', border: '1px solid #bbf7d0', textAlign: 'center' }}>
                                <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🎉</div>
                                <h3 style={{ color: '#15803d', fontWeight: 'bold', fontSize: '1.25rem', marginBottom: '8px' }}>Got it. Thanks for reaching out.</h3>
                                <p style={{ color: '#166534' }}>I'll reply personally within 2 business days — Kemal.</p>
                            </div>
                        ) : (
                            <form onSubmit={handleDemoSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Full Name *</label>
                                    <input type="text" required value={demoForm.name} onChange={e => setDemoForm({...demoForm, name: e.target.value})} style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', outline: 'none', transition: 'border 0.2s', backgroundColor: '#f8fafc' }} placeholder="Jane Doe" />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>School Email *</label>
                                    <input type="email" required value={demoForm.email} onChange={e => setDemoForm({...demoForm, email: e.target.value})} style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', outline: 'none', transition: 'border 0.2s', backgroundColor: '#f8fafc' }} placeholder="jane@yourdistrict.edu" />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Your role *</label>
                                    <select required value={demoForm.role} onChange={e => setDemoForm({...demoForm, role: e.target.value})} style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', outline: 'none', transition: 'border 0.2s', backgroundColor: '#f8fafc' }}>
                                        <option value="">Select one</option>
                                        <option value="teacher">Teacher</option>
                                        <option value="principal">Principal</option>
                                        <option value="district">District administrator</option>
                                        <option value="parent">Parent</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>School or district name</label>
                                    <input type="text" value={demoForm.district} onChange={e => setDemoForm({...demoForm, district: e.target.value})} style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', outline: 'none', transition: 'border 0.2s', backgroundColor: '#f8fafc' }} placeholder="Optional — helpful if you're asking about school pricing" />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Optional Message</label>
                                    <textarea value={demoForm.message} onChange={e => setDemoForm({...demoForm, message: e.target.value})} style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', outline: 'none', transition: 'border 0.2s', backgroundColor: '#f8fafc', minHeight: '100px', resize: 'vertical' }} placeholder="What would you like to know?" />
                                </div>
                                
                                {demoStatus === 'error' && (
                                    <div style={{ color: '#ef4444', fontSize: '0.9rem', fontWeight: '600' }}>There was an error submitting your request. Please try again.</div>
                                )}
                                
                                <button type="submit" disabled={demoStatus === 'submitting'} className="cta-primary" style={{
                                    padding: '16px',
                                    borderRadius: '8px',
                                    fontSize: '1.1rem',
                                    fontWeight: 'bold',
                                    marginTop: '10px',
                                    cursor: demoStatus === 'submitting' ? 'wait' : 'pointer',
                                    opacity: demoStatus === 'submitting' ? 0.7 : 1
                                }}>
                                    {demoStatus === 'submitting' ? 'Sending...' : 'Send →'}
                                </button>
                            </form>
                        )}
                    </div>
                </section>

            </main>

            {/* Footer */}
            <footer style={{
                padding: '60px 20px',
                textAlign: 'center',
                backgroundColor: '#ffffff',
                borderTop: '1px solid #e2e8f0',
                color: '#64748b'
            }}>
                <div style={{ marginBottom: '24px', display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px' }}>
                    <ComplianceBadge dataKey="act20"
                        bg="#f8fafc" color="#334155" border="#e2e8f0"
                        borderRadius="8px" padding="6px 14px" fontWeight="600" />
                    <ComplianceBadge dataKey="ferpa"
                        bg="#f8fafc" color="#334155" border="#e2e8f0"
                        borderRadius="8px" padding="6px 14px" fontWeight="600" />
                    <ComplianceBadge dataKey="mtss_idea"
                        bg="#f8fafc" color="#334155" border="#e2e8f0"
                        borderRadius="8px" padding="6px 14px" fontWeight="600" />
                </div>

                <p style={{ maxWidth: '680px', margin: '0 auto 20px auto', fontSize: '0.95rem', lineHeight: '1.7', color: '#475569' }}>
                    This platform supports early literacy assessment workflows. Wisconsin
                    districts using it for Act 20 notification workflows should verify their
                    compliance approach with their curriculum office. Student records remain
                    school-controlled and are handled under the Privacy Policy and any school
                    or district agreement. Not affiliated with any commercial screening product.
                </p>
                <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
                    <Link to="/about-data" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: '600' }}>View Data & SoR Methodology</Link>
                    <Link to="/trust-center" style={{ color: '#64748b', textDecoration: 'none', fontWeight: '600' }}>Trust Center</Link>
                    <Link to="/privacy" style={{ color: '#64748b', textDecoration: 'none', fontWeight: '600' }}>Privacy Policy</Link>
                    <Link to="/terms" style={{ color: '#64748b', textDecoration: 'none', fontWeight: '600' }}>Terms of Service</Link>
                </div>
                <p style={{ margin: '0 0 16px 0', fontSize: '0.9rem', color: '#475569' }}>
                    Questions, or school &amp; district pricing? Call{' '}
                    <a href="tel:+12625267582" style={{ color: '#3b82f6', fontWeight: '600', textDecoration: 'none' }}>
                        +1 (262) 526-7582
                    </a>
                </p>
                <p style={{ margin: '0 0 4px 0', fontSize: '0.85rem', color: '#94a3b8' }}>
                    Operated by Yilmaz Education LLC, Brookfield, WI
                </p>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#94a3b8' }}>
                    &copy; 2026 Yilmaz Education LLC. All rights reserved.
                </p>
            </footer>

        </div>
    );
}

export default LandingPage;
